/**
 * WAV Audio Encoder
 *
 * Utilities for encoding PCM audio data to WAV format.
 * Generates RIFF-compliant WAV files with configurable sample rate and bit depth.
 *
 * @module lib/audio/wav-encoder
 */

/**
 * WAV encoding configuration
 */
export interface WavEncoderConfig {
  /** Sample rate in Hz (default: 16000) */
  sampleRate: number;
  /** Number of channels: 1 for mono, 2 for stereo (default: 1) */
  numChannels: number;
  /** Bits per sample: 8 or 16 (default: 16) */
  bitsPerSample: number;
}

/**
 * Default WAV encoder configuration
 * Optimized for Whisper ASR: 16kHz, mono, 16-bit
 */
export const DEFAULT_WAV_CONFIG: WavEncoderConfig = {
  sampleRate: 16000,
  numChannels: 1,
  bitsPerSample: 16,
};

/**
 * Generate WAV file header (RIFF format)
 *
 * WAV Header Structure (44 bytes):
 * ```
 * Offset | Size | Field          | Value
 * -------|------|----------------|-------
 * 0      | 4    | ChunkID        | "RIFF"
 * 4      | 4    | ChunkSize      | 36 + dataSize
 * 8      | 4    | Format         | "WAVE"
 * 12     | 4    | Subchunk1ID    | "fmt "
 * 16     | 4    | Subchunk1Size  | 16
 * 20     | 2    | AudioFormat    | 1 (PCM)
 * 22     | 2    | NumChannels    | 1 or 2
 * 24     | 4    | SampleRate     | 16000, 44100, etc.
 * 28     | 4    | ByteRate       | SampleRate * NumChannels * BitsPerSample/8
 * 32     | 2    | BlockAlign     | NumChannels * BitsPerSample/8
 * 34     | 2    | BitsPerSample  | 8, 16, etc.
 * 36     | 4    | Subchunk2ID    | "data"
 * 40     | 4    | Subchunk2Size  | dataSize
 * ```
 *
 * @param dataSize - Size of PCM data in bytes
 * @param config - WAV encoder configuration
 * @returns ArrayBuffer containing 44-byte WAV header
 *
 * @example
 * ```typescript
 * const pcmData = new Int16Array(8000); // 0.5s at 16kHz
 * const dataSize = pcmData.byteLength; // 16000 bytes
 * const header = generateWavHeader(dataSize);
 * ```
 */
export function generateWavHeader(
  dataSize: number,
  config: WavEncoderConfig = DEFAULT_WAV_CONFIG
): ArrayBuffer {
  const { sampleRate, numChannels, bitsPerSample } = config;

  const byteRate = (sampleRate * numChannels * bitsPerSample) / 8;
  const blockAlign = (numChannels * bitsPerSample) / 8;

  const buffer = new ArrayBuffer(44);
  const view = new DataView(buffer);

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true); // ChunkSize (little-endian)
  writeString(view, 8, 'WAVE');

  // fmt sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 = PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  return buffer;
}

/**
 * Write ASCII string to DataView
 *
 * @param view - DataView to write to
 * @param offset - Byte offset
 * @param str - String to write (ASCII only)
 */
function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Convert Float32Array PCM data to Int16Array
 *
 * Web Audio API outputs Float32 samples in range [-1.0, 1.0].
 * WAV 16-bit PCM requires Int16 samples in range [-32768, 32767].
 *
 * @param float32Array - Float32 PCM data from AudioWorklet
 * @returns Int16Array suitable for WAV encoding
 *
 * @example
 * ```typescript
 * const float32Data = new Float32Array([0.5, -0.5, 1.0, -1.0]);
 * const int16Data = float32ToInt16(float32Data);
 * // Result: Int16Array([16384, -16384, 32767, -32768])
 * ```
 */
export function float32ToInt16(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length);

  for (let i = 0; i < float32Array.length; i++) {
    // Clamp to [-1.0, 1.0] to prevent overflow
    const sample = Math.max(-1, Math.min(1, float32Array[i]));

    // Convert to 16-bit integer
    // Multiply by 32767 (max positive) instead of 32768 to avoid asymmetry
    int16Array[i] = sample < 0 ? sample * 32768 : sample * 32767;
  }

  return int16Array;
}

/**
 * Encode Float32Array PCM data to WAV Blob
 *
 * Creates a complete WAV file with RIFF header + PCM data.
 * Each call produces an independent, playable WAV file.
 *
 * @param pcmData - Float32Array from AudioWorklet (range: [-1.0, 1.0])
 * @param config - WAV encoder configuration
 * @returns Blob containing complete WAV file
 *
 * @example
 * ```typescript
 * // Encode 500ms of audio at 16kHz
 * const pcmData = new Float32Array(8000); // 16kHz * 0.5s
 * const wavBlob = encodeWav(pcmData);
 *
 * // Send to server or play in browser
 * const audio = new Audio(URL.createObjectURL(wavBlob));
 * audio.play();
 * ```
 */
export function encodeWav(
  pcmData: Float32Array,
  config: WavEncoderConfig = DEFAULT_WAV_CONFIG
): Blob {
  // Convert Float32 → Int16
  const int16Data = float32ToInt16(pcmData);

  // Generate WAV header
  const dataSize = int16Data.byteLength;
  const header = generateWavHeader(dataSize, config);

  // Concatenate header + PCM data
  // Create new ArrayBuffer copy to satisfy TypeScript's strict type checking
  const pcmBuffer = new ArrayBuffer(int16Data.byteLength);
  const pcmView = new Uint8Array(pcmBuffer);
  pcmView.set(new Uint8Array(int16Data.buffer, int16Data.byteOffset, int16Data.byteLength));

  return new Blob([header, pcmBuffer], { type: 'audio/wav' });
}

/**
 * Validate WAV encoder configuration
 *
 * @param config - Configuration to validate
 * @throws Error if configuration is invalid
 */
export function validateWavConfig(config: WavEncoderConfig): void {
  if (config.sampleRate <= 0) {
    throw new Error(`Invalid sample rate: ${config.sampleRate}`);
  }

  if (config.numChannels !== 1 && config.numChannels !== 2) {
    throw new Error(`Invalid number of channels: ${config.numChannels}`);
  }

  if (config.bitsPerSample !== 8 && config.bitsPerSample !== 16) {
    throw new Error(`Invalid bits per sample: ${config.bitsPerSample}`);
  }
}
